import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';
import { Card } from '../components/ui/Card';

type LeadNote = {
  id: string;
  user_id: string | null;
  lead_id: number | null;
  body: string;
  created_on: string | null;
  created_by_staff_external_id: number | null;
  source: string | null;
  created_at: string;
  status?: string | null;
  zonedm_source_note_id?: string | null;
  external_note_id?: number | null;
  raw_payload?: any;
  contact_name?: string | null;
  contact_id?: string | null;
  _zonedmAuthor?: string | null;
};

const parseNoteDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const extractWebhookBody = (payload: any): string => {
  const noteBody = payload?.note?.body || payload?.note?.Body || payload?.body || payload?.Body || payload?.message || '';
  return typeof noteBody === 'string' ? noteBody.trim() : '';
};

const extractContactName = (payload: any): string | null => {
  const fullName =
    payload?.full_name ||
    payload?.contact?.name ||
    payload?.contact?.fullName ||
    [payload?.first_name, payload?.last_name].filter(Boolean).join(' ') ||
    [payload?.contact?.firstName, payload?.contact?.lastName].filter(Boolean).join(' ');

  if (!fullName || typeof fullName !== 'string') return null;
  const trimmed = fullName.trim();
  return trimmed ? trimmed : null;
};

const extractContactId = (payload: any): string | null => {
  const id = payload?.contact_id || payload?.contactId || payload?.contact?.id || null;
  if (id == null) return null;
  const normalized = String(id).trim();
  return normalized || null;
};

export default function LeadNotes() {
  const [leadId, setLeadId] = React.useState('');
  const [contactSearch, setContactSearch] = React.useState('');
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');

  const { data: notes, isLoading } = useQuery({
    queryKey: ['lead-notes', leadId, contactSearch, startDate, endDate],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const dbNotesQuery = supabase
        .from('lead_notes')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_on', { ascending: false });

      const webhookNotesQuery = supabase
        .from('webhook_logs')
        .select('id, user_id, status, payload, created_at')
        .eq('user_id', session.user.id)
        .eq('type', 'zonedm_note_event')
        .order('created_at', { ascending: false })
        .limit(2000);

      const [{ data: dbNotes, error: dbError }, { data: webhookLogs, error: webhookError }] = await Promise.all([
        dbNotesQuery,
        webhookNotesQuery,
      ]);

      if (dbError) throw dbError;
      if (webhookError) throw webhookError;

      const dbMappedNotes: LeadNote[] = (dbNotes || []).map((note: any) => {
        const payload = note?.raw_payload || {};
        return {
          ...note,
          contact_name: extractContactName(payload),
          contact_id: extractContactId(payload),
        } as LeadNote;
      });

      const existingExternalIds = new Set(
        dbMappedNotes
          .map((n: any) => n.zonedm_source_note_id)
          .filter(Boolean)
      );

      const webhookMappedNotes: LeadNote[] = (webhookLogs || [])
        .map((log: any) => {
          const payload = log.payload || {};
          const body = extractWebhookBody(payload);
          if (!body) return null;

          const noteId =
            payload?.note?.id ||
            payload?.note?._id ||
            payload?.noteId ||
            payload?.note_id ||
            payload?.id ||
            null;

          if (noteId && existingExternalIds.has(String(noteId))) {
            return null;
          }

          const createdOn =
            payload?.note?.dateAdded ||
            payload?.note?.date_added ||
            payload?.dateAdded ||
            payload?.date_added ||
            payload?.date_created ||
            payload?.created_at ||
            log.created_at;

          // LeadId is resolved server-side — not present in raw HL workflow payload.
          // PatientId is often "" which Number() turns to 0; exclude it and treat 0 as null.
          const leadIdRaw = payload?.LeadId ?? payload?.leadId ?? payload?.lead_id ?? null;
          const leadIdNum =
            leadIdRaw != null && leadIdRaw !== '' && !Number.isNaN(Number(leadIdRaw)) && Number(leadIdRaw) !== 0
              ? Number(leadIdRaw)
              : null;

          // 4D staff ID (only present on inbound 4D webhooks, not HL workflow events)
          const staffIdRaw = payload?.CreatedByStaffId ?? payload?.created_by_staff_external_id ?? null;
          const staffIdNum =
            staffIdRaw != null && staffIdRaw !== '' && !Number.isNaN(Number(staffIdRaw))
              ? Number(staffIdRaw)
              : null;

          // ZoneDM workflow payloads include the note author in payload.user
          const zonedmAuthor = payload?.user
            ? [payload.user.firstName, payload.user.lastName].filter(Boolean).join(' ') || payload.user.email || null
            : null;

          return {
            id: `webhook-${log.id}`,
            user_id: log.user_id || null,
            lead_id: leadIdNum,
            body,
            created_on: createdOn ? String(createdOn) : null,
            created_by_staff_external_id: staffIdNum,
            _zonedmAuthor: zonedmAuthor,
            source: 'zonedm',
            created_at: log.created_at,
            status: log.status || null,
            zonedm_source_note_id: noteId ? String(noteId) : null,
            external_note_id: null,
            contact_name: extractContactName(payload),
            contact_id: extractContactId(payload),
          } as LeadNote;
        })
        .filter(Boolean) as LeadNote[];

      const mergedNotes: LeadNote[] = [...dbMappedNotes, ...webhookMappedNotes];

      const leadIdFilter = leadId.trim();
      const contactTokens = contactSearch
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);
      const leadIdNumber = Number(leadIdFilter);
      const startDateValue = startDate ? new Date(startDate) : null;
      const endDateValue = endDate ? new Date(endDate) : null;
      if (endDateValue) {
        endDateValue.setHours(23, 59, 59, 999);
      }

      return mergedNotes
        .filter((note) => {
          if (leadIdFilter) {
            if (Number.isNaN(leadIdNumber)) return false;
            if (note.lead_id !== leadIdNumber) return false;
          }

          if (contactTokens.length > 0) {
            const contactHaystack = `${note.contact_name || ''} ${note.contact_id || ''}`.toLowerCase();
            if (!contactTokens.every((token) => contactHaystack.includes(token))) {
              return false;
            }
          }

          const noteDate = parseNoteDate(note.created_on) || parseNoteDate(note.created_at);
          if (!noteDate) return false;
          if (startDateValue && noteDate < startDateValue) return false;
          if (endDateValue && noteDate > endDateValue) return false;
          return true;
        })
        .sort((a, b) => {
          const timeA = (parseNoteDate(a.created_on) || parseNoteDate(a.created_at) || new Date(0)).getTime();
          const timeB = (parseNoteDate(b.created_on) || parseNoteDate(b.created_at) || new Date(0)).getTime();
          return timeB - timeA;
        });
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Lead Notes</h1>
        <p className="text-gray-600">Notes posted to 4D EMR leads</p>
      </div>

      <Card>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Lead ID</label>
            <input
              type="text"
              value={leadId}
              onChange={(e) => setLeadId(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder="LeadId"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Name</label>
            <input
              type="text"
              value={contactSearch}
              onChange={(e) => setContactSearch(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder="Name or contact ID"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>
      </Card>

      <Card>
        {isLoading ? (
          <div className="text-gray-500">Loading lead notes...</div>
        ) : notes && notes.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created On
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lead ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Staff ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Body
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {notes.map((note) => (
                  <tr key={note.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {note.created_on ? format(new Date(note.created_on), 'PP p') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {note.lead_id ?? '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>{note.contact_name || '-'}</div>
                      {note.contact_id && (
                        <div className="text-xs text-gray-500 font-mono">{note.contact_id}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {note.created_by_staff_external_id
                        ? String(note.created_by_staff_external_id)
                        : (note as any)._zonedmAuthor
                          ? <span className="text-xs text-gray-600">{(note as any)._zonedmAuthor}</span>
                          : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 max-w-xl truncate">
                      {note.body}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                      {note.source || '-'}{note.status ? ` (${note.status})` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-gray-500">No lead notes found.</div>
        )}
      </Card>
    </div>
  );
}
